import React, { useState } from 'react';
import { MSForm } from '../Travelbingo';
import { ExcelLoader, MSFormsLoader, RedaxoLoader, BuyerEntry } from './dataloaders';

type DataSource = 'excel' | 'msforms' | 'redaxo';

interface DatenLadenProps {
    onDataLoaded: (loaded: boolean) => void;
    onFormSelected?: (form: MSForm | null) => void;
    selectedForm?: MSForm | null;
    onBuyerDataLoaded?: (buyerData: BuyerEntry[]) => void;
    onRandomnessSeedLoaded?: (seed: string) => void;
}

const DatenLaden: React.FC<DatenLadenProps> = ({ onDataLoaded, onFormSelected, selectedForm, onBuyerDataLoaded, onRandomnessSeedLoaded }) => {
    const [selectedSource, setSelectedSource] = useState<DataSource | null>(null);

    const buttonStyle = (source: DataSource, enabled: boolean) => ({
        padding: '16px 24px',
        fontSize: '16px',
        fontWeight: 500 as const,
        backgroundColor: selectedSource === source ? '#007bff' : (enabled ? 'white' : '#f8f9fa'),
        color: selectedSource === source ? 'white' : (enabled ? '#333' : '#999'),
        border: selectedSource === source ? '2px solid #007bff' : '2px solid #dee2e6',
        borderRadius: '8px',
        cursor: enabled ? 'pointer' : 'not-allowed',
        opacity: enabled ? 1 : 0.6,
        transition: 'all 0.2s ease',
        minWidth: '200px',
        textAlign: 'center' as const
    });

    const handleSourceSelect = (source: DataSource, enabled: boolean) => {
        if (!enabled) return;
        setSelectedSource(source);
        // Reset data loaded state when changing source
        onDataLoaded(false);
        if (onFormSelected) {
            onFormSelected(null);
        }
        if (onBuyerDataLoaded) {
            onBuyerDataLoaded([]);
        }
        if (onRandomnessSeedLoaded) {
            onRandomnessSeedLoaded('');
        }
    };

    const renderLoader = () => {
        switch (selectedSource) {
            case 'excel':
                return <ExcelLoader onDataLoaded={onDataLoaded} onFormSelected={onFormSelected} selectedForm={selectedForm} onBuyerDataLoaded={onBuyerDataLoaded} onRandomnessSeedLoaded={onRandomnessSeedLoaded} />;
            case 'msforms':
                return <MSFormsLoader onDataLoaded={onDataLoaded} onFormSelected={onFormSelected} selectedForm={selectedForm} onBuyerDataLoaded={onBuyerDataLoaded} onRandomnessSeedLoaded={onRandomnessSeedLoaded} />;
            case 'redaxo':
                return <RedaxoLoader onDataLoaded={onDataLoaded} onFormSelected={onFormSelected} selectedForm={selectedForm} onBuyerDataLoaded={onBuyerDataLoaded} onRandomnessSeedLoaded={onRandomnessSeedLoaded} />;
            default:
                return null;
        }
    };

    return (
        <div className="step-content">
            <h2>Daten laden</h2>
            <p>Wählen Sie eine Datenquelle aus:</p>
            
            {/* Data source selection buttons */}
            <div style={{ 
                display: 'flex', 
                gap: '16px', 
                marginTop: '24px', 
                marginBottom: '24px',
                flexWrap: 'wrap'
            }}>
                <button
                    onClick={() => handleSourceSelect('excel', true)}
                    style={buttonStyle('excel', true)}
                    onMouseEnter={(e) => {
                        if (selectedSource !== 'excel') {
                            e.currentTarget.style.borderColor = '#007bff';
                            e.currentTarget.style.backgroundColor = '#f0f7ff';
                        }
                    }}
                    onMouseLeave={(e) => {
                        if (selectedSource !== 'excel') {
                            e.currentTarget.style.borderColor = '#dee2e6';
                            e.currentTarget.style.backgroundColor = 'white';
                        }
                    }}
                >
                    📊 Excel hochladen
                </button>
                
                <button
                    style={buttonStyle('msforms', false)}
                    disabled
                    title="Diese Funktion ist noch nicht verfügbar"
                >
                    📝 MS Forms laden
                </button>
                
                <button
                    style={buttonStyle('redaxo', false)}
                    disabled
                    title="Diese Funktion ist noch nicht verfügbar"
                >
                    🔗 Redaxo laden
                </button>
            </div>

            {/* Render the selected loader */}
            {selectedSource && (
                <div style={{ marginTop: '20px' }}>
                    {renderLoader()}
                </div>
            )}

            {/* Hint when no source is selected */}
            {!selectedSource && (
                <div style={{ 
                    marginTop: '40px', 
                    padding: '20px', 
                    backgroundColor: '#e7f3ff', 
                    borderRadius: '8px',
                    border: '1px solid #b8daff',
                    color: '#004085'
                }}>
                    <p style={{ margin: 0 }}>
                        💡 <strong>Hinweis:</strong> Bitte wählen Sie eine Datenquelle aus, um fortzufahren.
                        Derzeit ist nur der Excel-Upload verfügbar.
                    </p>
                </div>
            )}
        </div>
    );
};

export default DatenLaden;
